import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { App } from "antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { TMutationConfig } from "@/hooks/useMutate";

type TReactQueryWrapperProps = { children: React.ReactNode };

function ReactQueryWrapper({ children }: TReactQueryWrapperProps) {
  const { message: toastMessage } = App.useApp();
  const { t } = useTranslation();
  const [queryClient] = useState(() => new QueryClient({
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        const meta = mutation.meta as TMutationConfig;
        if (!meta) {
          return;
        }

        if (meta?.useSuccessMessage && meta?.successMessage) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          toastMessage.success(t(meta.successMessage as any));
          return;
        }
        if (meta?.useSuccessMessage && !meta?.successMessage) {
          toastMessage.success(t("success"));
        }
      },
      onError: (error, _vars, _context, mutation) => {
        const meta = mutation.meta as TMutationConfig;
        const errorObject = error as unknown;
        if (!meta?.useErrorMessage) {
          return;
        };
        let message = "";
        if (meta?.errorMessage) {
          message = meta.errorMessage;
        }
        else if (
          typeof errorObject === "object"
          && (errorObject as { message: string }).message
        ) {
          message = (errorObject as { message: string }).message;
        }
        else if (typeof errorObject === "string") {
          if (errorObject.startsWith("Error: ")) {
            message = errorObject.split("Error: ")[1];
          }
          else {
            message = errorObject;
          };
        }
        else {
          message = t("error");
        }
        toastMessage.error(message);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      {children}
    </QueryClientProvider>
  );
}

export default ReactQueryWrapper;
